import { FC, useEffect, useState } from "react";
import { ControllerRenderProps } from "react-hook-form";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils/utils";
import { FormLabel } from "./ui/form";

interface Props {
  label: string;
  field: ControllerRenderProps<any>;
  placeholder: string;
  options: { value: string; label: string }[];
}

const FormSelect: FC<Props> = ({ label, field, placeholder, options }) => {
  const [open, setOpen] = useState(false);

  const handleChange = (value: string) => {
    const index = field.value.indexOf(value);
    if (index === -1) {
      field.onChange([...field.value, value]);
    } else {
      field.onChange(field.value.filter((v: string) => v !== value));
    }
  };

  return (
    <div className="w-full relative cursor-pointer">
      <FormLabel className="m-0 p-0 text-[14px] pb-2 text-[#6F7582]">
        {label}
      </FormLabel>
      <div
        className={cn(
          "flex w-full border rounded-[6px] px-3 py-1 select-none items-center",
          open && "rounded-b-none",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          field.value.length <= 0 ? "justify-between" : "justify-start"
        )}
        data-state={open ? "open" : "closed"}
        onClick={() => setOpen((o) => true)}
      >
        {field.value.length <= 0 ? (
          <>
            <p className="m-0 p-0 text-[#717680] text-[14px]">{placeholder}</p>
            <ChevronDown color="#717680" size={14} />
          </>
        ) : (
          <div className="flex gap-2">
            {field.value.map((v: string) => (
              <div
                key={v}
                className="text-[12px] rounded-2xl border px-2 flex justify-center items-center text-center text-[#363F72] bg-[#F8F9FC] gap-1"
              >
                <p className="m-0 p-0">
                  {options.find((o) => o.value === v)?.label}
                </p>
                <p
                  className="m-0 p-0 font-semibold"
                  onClick={() => handleChange(v)}
                >
                  x
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      {open && (
        <div className="flex flex-col max-h-[200px] scroll-auto bg-white border-b border-x rounded-b-xl select-none">
          {options.map((opt, i) => (
            <div
              className={cn(
                "flex py-1 px-3",
                i !== options.length - 1 && "border-b"
              )}
              onClick={() => handleChange(opt.value)}
              key={i}
            >
              <div className="flex gap-2 justify-center items-center text-center">
                <input
                  type="checkbox"
                  className="border-[#717680] drop-shadow-none shadow-none p-0 m-0 translate-y-[4px]"
                  checked={field.value.includes(opt.value)}
                />
                <p className="m-0 p-0 text-[#717680] text-[14px]">
                  {opt.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FormSelect;
